<template>
  <dialog ref="dialog" class="simple-dialog" @close="handleClose" @click="handleBackdropClick">
    <div class="simple-dialog-modal" :class="{ prevent: preventClose, mobile: isMobile, bg: background }">
      <div disabled-v-on-click-outside="closeModal" class="simple-dialog-content">
        <div class="bg-image hue-rotate" :style="getBgStyle" />

        <slot />

        <div v-if="!preventClose && !hideBack" class="back-button" @click="handleClose">
          <GamIcon :size="GamIconSize.NORMAL" :name="GamIconName.X_CLOSE" is-dark />
        </div>
      </div>
    </div>

    <!-- Outside the card, so a layer here covers the dialog rather than the card. -->
    <slot name="overlay" />
  </dialog>
</template>

<script setup lang="ts">
import { isMobile } from '@/core/helpers/ui.helper';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import type { DialogBackgrounds } from '@/views/composables/constants/dialog.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import { computed, onBeforeUnmount, onMounted, ref, type StyleValue } from 'vue';
import GamIcon from '../GamIcon.vue';

export interface SimpleDialogProps {
  preventClose?: boolean;
  hideBack?: boolean;
  background?: DialogBackgrounds;
}

const props = defineProps<SimpleDialogProps>();
const emit = defineEmits([GamComponentsEmits.CLOSE]);

const dialog = ref<HTMLDialogElement>();

onMounted(() => {
  dialog.value?.showModal();

  if (dialog.value) {
    dialog.value?.addEventListener('cancel', (event) => {
      if (props.preventClose) {
        event.preventDefault();
      }
    });
  }
});

onBeforeUnmount(() => {
  if (dialog.value?.open) {
    dialog.value.close();
  }
});

const handleClose = (e: Event): void => {
  if (props.preventClose) {
    e?.preventDefault();
    return;
  }

  emit(GamComponentsEmits.CLOSE);
};

const handleBackdropClick = (e: MouseEvent): void => {
  if (props.preventClose) return;
  if (e.target === dialog.value) {
    emit(GamComponentsEmits.CLOSE);
  }
};

const getBgStyle = computed((): StyleValue => {
  const bg = props.background ? `/images/dialogs/${props.background}` : undefined;
  return {
    backgroundImage: `url(${bg})`,
  };
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.simple-dialog {
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  outline: 0;

  &::-webkit-scrollbar {
    display: none;
  }
  background: none;
  border: 0;
  margin: 0 auto;
  transition: visibility var(--transition-loader);
  color: var(--color-white-100);
  position: relative;

  .bg-image {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    pointer-events: none;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center -54px;
    background-color: var(--color-dark-700);
    background-color: var(--color-dark-600);
    border-radius: var(--radius-large);
  }

  &::backdrop {
    background: rgba(23, 23, 23, 0.75);
  }

  .simple-dialog-modal {
    display: flex;
    position: relative;
    width: auto;
    max-width: 500px;
    min-height: 100%;
    pointer-events: none;
    align-items: center;
    justify-content: center;
    margin: 0 auto;

    &.mobile {
      max-width: 88vw;

      .simple-dialog-content {
        padding-left: var(--spacing-xl);
        padding-right: var(--spacing-xl);
      }
    }

    &.bg {
      .simple-dialog-content {
        min-height: 600px;
      }
    }

    .simple-dialog-content {
      position: relative;
      display: flex;
      flex-direction: column;
      width: fit-content;
      pointer-events: auto;
      align-items: center;
      justify-content: flex-end;
      flex-shrink: 0;
      padding: var(--spacing-xl) var(--spacing-menu) var(--spacing-xl) var(--spacing-menu);
      border-radius: var(--radius-large);
      background: var(--color-dark-700);
      background-clip: padding-box;
      background-size: cover !important;
      z-index: 100;
      border: 1px solid var(--color-white-10);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);

      .back-button {
        position: absolute;
        bottom: -16px;
        background-color: var(--color-white-100);
        border-radius: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        cursor: pointer;

        @include hover {
          border: 2px solid var(--color-pink);
        }
      }
    }

    &.prevent {
      .simple-dialog-content {
        padding: var(--spacing-menu);
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.back-button {
  z-index: 5;
}
</style>
